import React, { createContext, useState, useEffect } from 'react';
import { fetchWithAuth } from '../hooks/authHelpers';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Load cached theme immediately for fast rendering
    const cachedTheme = localStorage.getItem('userTheme');
    return cachedTheme || 'Anubis';
  });
  const [backgroundVideo, setBackgroundVideo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Apply cached theme immediately to prevent flash
    const cachedTheme = localStorage.getItem('userTheme');
    if (cachedTheme) {
      applyTheme(cachedTheme);
      setTheme(cachedTheme);
    } else {
      applyTheme('Anubis');
      setTheme('Anubis');
    }

    // Then validate the cached theme with the server
    validateAndFetchTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAndFetchTheme = async () => {
    try {
      setLoading(true);
      const cachedTheme = localStorage.getItem('userTheme');

      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-theme`, 'GET');

      if (response.ok) {
        const themeData = await response.json();

        // Check if cached theme matches server theme
        if (cachedTheme && cachedTheme === themeData.name) {
          // Cached theme is valid, no need to change anything
        } else {
          // Cached theme is invalid or different, update to server theme
          applyTheme(themeData.name);
          setTheme(themeData.name);
          localStorage.setItem('userTheme', themeData.name);
        }
      } else if (response.status === 404) {
        // No theme selected on server
        if (cachedTheme && cachedTheme !== 'Anubis') {
          // User has invalid cached theme, clear it and use default
          localStorage.removeItem('userTheme');
          applyTheme('Anubis');
          setTheme('Anubis');
        }
        // If cached is already 'Anubis' or null, no change needed
      } else {
        throw new Error('Failed to fetch theme');
      }
    } catch (error) {
      setError(error.message);

      // On error, clear cache and use default to be safe
      const cachedTheme = localStorage.getItem('userTheme');
      if (cachedTheme) {
        localStorage.removeItem('userTheme');
        applyTheme('Anubis');
        setTheme('Anubis');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (option) => {
    // Update the CSS variables based on the selected theme
    const root = document.documentElement;
    switch (option) {
      case 'Anubis':
        root.style.setProperty('--main', 'rgba(223, 143, 23, 1)');
        root.style.setProperty('--maind', 'rgba(182, 116, 18, 1)');
        root.style.setProperty('--maindd', 'rgba(182, 116, 18, 0.5)');
        root.style.setProperty('--mainb', 'rgba(255, 163, 26, 1)');
        root.style.setProperty('--maintransparent', 'rgba(182, 116, 18, 0.10)');
        setBackgroundVideo('/anubis-video.mp4');
        break;
      case 'Nuke':
        root.style.setProperty('--main', 'rgba(0, 102, 204, 1)');
        root.style.setProperty('--maind', 'rgba(0, 89, 179, 1)');
        root.style.setProperty('--maindd', 'rgba(0, 89, 179, 0.5)');
        root.style.setProperty('--mainb', 'rgba(0, 127, 255, 1)');
        root.style.setProperty('--maintransparent', 'rgba(0, 89, 179, 0.10)');
        setBackgroundVideo('/nuke-video.mp4');
        break;
      case 'Ancient':
        root.style.setProperty('--main', 'rgba(0, 102, 0, 1)');
        root.style.setProperty('--maind', 'rgba(0, 102, 0, 1)');
        root.style.setProperty('--maindd', 'rgba(0, 102, 0, 0.5)');
        root.style.setProperty('--mainb', 'rgba(0, 204, 0, 1)');
        root.style.setProperty('--maintransparent', 'rgba(0, 153, 0, 0.10)');
        setBackgroundVideo('/ancient-video.mp4');
        break;
      case 'Train':
        root.style.setProperty('--main', 'rgba(223, 191, 23, 1)');       // bright yellow
        root.style.setProperty('--maind', 'rgba(182, 156, 18, 1)');      // darker yellow
        root.style.setProperty('--maindd', 'rgba(182, 156, 18, 0.5)');   // semi-transparent darker yellow
        root.style.setProperty('--mainb', 'rgba(255, 214, 26, 1)');      // light vibrant yellow
        root.style.setProperty('--maintransparent', 'rgba(182, 156, 18, 0.10)'); // transparent
        setBackgroundVideo('/train-video.mp4');
        break;
      default:
        root.style.setProperty('--main', 'rgba(223, 143, 23, 1)');
        root.style.setProperty('--maind', 'rgba(182, 116, 18, 1)');
        root.style.setProperty('--maindd', 'rgba(182, 116, 18, 0.5)');
        root.style.setProperty('--mainb', 'rgba(255, 163, 26, 1)');
        root.style.setProperty('--maintransparent', 'rgba(182, 116, 18, 0.10)');
        setBackgroundVideo('/anubis-video.mp4');
        break;
    }
  };

  const changeTheme = async (themeId) => {
    try {
      setError(null);
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cosmetics/selected-theme`, 'PATCH', {
        themeId: themeId
      });

      if (response.ok) {
        const data = await response.json();
        // Apply the theme if successful
        applyTheme(data.theme.name);
        setTheme(data.theme.name);
        // Cache the new theme
        localStorage.setItem('userTheme', data.theme.name);
        console.log('Theme changed:', data.theme.name);
        return { success: true, message: data.message };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update theme');
      }
    } catch (error) {
      console.error('Error updating theme');
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const refreshTheme = () => {
    validateAndFetchTheme();
  };

  // Function to temporarily apply another user's theme (for profile viewing)
  const applyUserTheme = async (userNumber) => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/cosmetics/${userNumber}/selected-theme`, 'GET');

      if (response.ok) {
        const themeData = await response.json();
        applyTheme(themeData.name);
        setTheme(themeData.name);
        // Don't update the main theme state or localStorage for temporary themes
        return { success: true, theme: themeData.name };
      } else if (response.status === 404) {
        // User has no theme, apply default
        applyTheme('Anubis');
        setTheme('Anubis');
        return { success: true, theme: 'Anubis' };
      } else {
        throw new Error('Failed to fetch user theme');
      }
    } catch (error) {
      console.error('Error fetching user theme');
      // Fallback to current user's theme
      const cachedTheme = localStorage.getItem('userTheme') || 'Anubis';
      applyTheme(cachedTheme);
      setTheme(cachedTheme);
      return { success: false, message: error.message };
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme, backgroundVideo, loading, error, refreshTheme, applyUserTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};